import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import moment from 'moment';
import {DateRangePicker} from '@forecast-it/design-system';
import {isInvoiceDateRangeAllowed} from '../../../components/insights/invoicing/importFromXero';
import {RangeBox, VisuallyHidden} from './XeroInvoiceImportModal.styled';

export const IMPORT_DATE_RANGE_DEBOUNCE_MS = 400;

// Opening the calendar moves focus to the page for a moment (about 80 ms) before the popup takes it,
// so a blur to nowhere waits this long for focus to come back before counting as leaving.
export const LEAVE_SETTLE_MS = 250;

// The picker reports a year as it is typed (2, 20, 202, 2026), so an early year is one still being typed.
const EARLIEST_YEAR = 1900;

export type InvoiceDateRange = {startDate: string; endDate: string};

type Props = {
	// The range the list was last asked for.
	range: InvoiceDateRange;
	onAccept: (range: InvoiceDateRange) => void;
};

const isCompleteDay = (value: string | undefined): value is string => {
	const day = moment(value, 'YYYY-MM-DD', true);
	return day.isValid() && day.year() >= EARLIEST_YEAR;
};

/**
 * The Invoice date range of the import modal. The picker reports every segment edit, so a range is
 * judged once edits pause: an unfinished one, or one that ends before it starts, is left as typed; a
 * valid one is handed on. One over two months is refused with a message; while the user is still in
 * the picker - its fields or its calendar - the dates stay (typing a new start first passes through a
 * long range), and once focus leaves the picker a range still waiting for its pause is judged at
 * once, and a refused one goes back to the last accepted range.
 */
export const ImportDateRange = ({range, onAccept}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const [draft, setDraft] = useState<InvoiceDateRange>(range);
	const [rejected, setRejected] = useState(false);
	// A new key remounts the picker, which otherwise keeps showing the refused dates.
	const [pickerKey, setPickerKey] = useState(0);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const rangeRef = useRef(range);
	rangeRef.current = range;
	// The user is in the picker (its fields or its calendar), so a refusal waits for them to leave.
	const typing = useRef(false);
	const refusedWhileTyping = useRef(false);
	// The range waiting for its pause, judged at once if the user leaves first.
	const pendingRange = useRef<InvoiceDateRange | null>(null);
	const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const boxRef = useRef<HTMLDivElement>(null);

	const cancelPending = (): void => {
		pendingRange.current = null;
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	};

	const cancelLeave = (): void => {
		if (leaveTimerRef.current) {
			clearTimeout(leaveTimerRef.current);
			leaveTimerRef.current = null;
		}
	};

	useEffect(
		() => () => {
			cancelPending();
			cancelLeave();
		},
		[]
	);

	useEffect(() => {
		setDraft(range);
	}, [range.startDate, range.endDate]);

	// Back to the last accepted range; a new key remounts the picker on it.
	const revert = (): void => {
		refusedWhileTyping.current = false;
		setDraft(rangeRef.current);
		setPickerKey(key => key + 1);
	};

	const judge = (startDate: string, endDate: string): void => {
		const accepted = rangeRef.current;
		if (!isInvoiceDateRangeAllowed(startDate, endDate)) {
			setRejected(true);
			if (typing.current) {
				refusedWhileTyping.current = true;
				return;
			}
			revert();
			return;
		}
		refusedWhileTyping.current = false;
		setRejected(false);
		if (startDate !== accepted.startDate || endDate !== accepted.endDate) {
			onAccept({startDate, endDate});
		}
	};

	const handleChange = ({startDate, endDate}: {startDate?: string; endDate?: string}): void => {
		setDraft({startDate: startDate ?? '', endDate: endDate ?? ''});
		cancelPending();
		if (!isCompleteDay(startDate) || !isCompleteDay(endDate) || endDate < startDate) {
			return;
		}
		pendingRange.current = {startDate, endDate};
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			pendingRange.current = null;
			judge(startDate, endDate);
		}, IMPORT_DATE_RANGE_DEBOUNCE_MS);
	};

	const left = (): void => {
		leaveTimerRef.current = null;
		// The calendar is open (its button says so): the user is still in the picker, however long focus
		// takes to settle in the popup while the modal's focus trap and react-aria hand it back and forth.
		if (boxRef.current?.querySelector('[aria-expanded="true"]')) {
			return;
		}
		typing.current = false;
		const waiting = pendingRange.current;
		if (waiting) {
			cancelPending();
			judge(waiting.startDate, waiting.endDate);
		} else if (refusedWhileTyping.current) {
			revert();
		}
	};

	// React focus events bubble through portals, so focus moving into the calendar popup arrives here as
	// a blur then a focus. Leaving is decided a tick later, once no focus inside the picker followed.
	const handleFocus = (): void => {
		cancelLeave();
		typing.current = true;
	};

	const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
		cancelLeave();
		// Focus moved to another element: decided next tick. To nowhere (the page): wait for it to settle.
		const movedToElement = event.relatedTarget instanceof HTMLElement && event.relatedTarget !== document.body;
		leaveTimerRef.current = setTimeout(left, movedToElement ? 0 : LEAVE_SETTLE_MS);
	};

	const message = rejected ? formatMessage({id: 'xero_invoice_import.range_limit'}) : undefined;

	return (
		<RangeBox ref={boxRef} onFocus={handleFocus} onBlur={handleBlur} data-cy="xero-import-date-range">
			<DateRangePicker
				key={pickerKey}
				aria-label={formatMessage({id: 'xero_invoice_import.date_range_aria'})}
				startDate={draft.startDate}
				endDate={draft.endDate}
				onChange={handleChange}
				errorText={message}
				width={'flex'}
				data-cy="xero-import-invoice-date"
			/>
			{/* The picker's errorText is not announced; this says it to a screen reader. */}
			<VisuallyHidden role="status">{message}</VisuallyHidden>
		</RangeBox>
	);
};
